import { useState, useEffect } from 'react';
import { Library, Search, Send, Eye, X, Sparkles, Filter } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const AUDIENCE_LABELS = {
  'b2b-contacts66': { label: 'B2B · Contacts66', icon: '🤝', color: 'purple' },
  'b2b-entreprise': { label: 'B2B · Entreprise', icon: '🏢', color: 'blue' },
  'b2c-restaurant': { label: 'B2C · Restaurant', icon: '🍽️', color: 'emerald' },
  'b2c-vip': { label: 'B2C · VIP', icon: '⭐', color: 'amber' },
};

const SAISON_LABELS = {
  'printemps': { label: 'Printemps', icon: '🌸' },
  'ete': { label: 'Été', icon: '☀️' },
  'automne': { label: 'Automne', icon: '🍂' },
  'hiver': { label: 'Hiver', icon: '❄️' },
  'toutes-saisons': { label: 'Toutes saisons', icon: '📅' },
};

const OCCASION_LABELS = {
  'lancement': '🚀 Lancement',
  'relance-j5': '🔁 Relance J+5',
  'relance-j12': '📌 Relance J+12',
  'relance-j20': '⚡ Last call J+20',
  'menu-saison': '🍽️ Menu saison',
  'reactivation': '💎 Réactivation',
  'anniversaire': '🎂 Anniversaire',
  'saint-valentin': '❤️ Saint-Valentin',
  'fete-meres': '💐 Fête des mères',
  'fete-peres': '👔 Fête des pères',
  'noel': '🎄 Noël',
  'nouvel-an': '🎉 Nouvel An',
  'evenement': '🎪 Événement',
  'fidelite': '🎁 Fidélité',
};

export default function EmailTemplatesLibrary() {
  const [templates, setTemplates] = useState([]);
  const [facets, setFacets] = useState({ audiences: [], saisons: [], occasions: [] });
  const [filterAudience, setFilterAudience] = useState('');
  const [filterSaison, setFilterSaison] = useState('');
  const [filterOccasion, setFilterOccasion] = useState('');
  const [search, setSearch] = useState('');
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams();
    if (filterAudience) params.set('audience', filterAudience);
    if (filterSaison) params.set('saison', filterSaison);
    if (filterOccasion) params.set('occasion', filterOccasion);
    fetch(`${API}/api/email-templates-library?${params}`)
      .then(r => r.json())
      .then(d => { setTemplates(d.templates || []); setFacets(d.facets || {}); })
      .catch(() => toast.error('Erreur chargement'));
  }, [filterAudience, filterSaison, filterOccasion]);

  const visibleTemplates = templates.filter(t => {
    if (!search) return true;
    const q = search.toLowerCase();
    return t.name.toLowerCase().includes(q) || t.subject.toLowerCase().includes(q) || t.body.toLowerCase().includes(q);
  });

  const applyTemplate = (tpl) => {
    // Save selected template in sessionStorage and navigate to campagne creation
    sessionStorage.setItem('selected_template', JSON.stringify({ subject: tpl.subject, body: tpl.body }));
    sessionStorage.setItem('patron_tab', 'crm-campagne');
    window.location.href = '/patron';
  };

  const clearFilters = () => { setFilterAudience(''); setFilterSaison(''); setFilterOccasion(''); setSearch(''); };

  return (
    <div className="space-y-4" data-testid="templates-library">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center">
            <Library size={16} className="text-white" />
          </div>
          <div>
            <h2 className="text-base font-bold text-[rgb(var(--lm-text))]">Bibliothèque de templates</h2>
            <p className="text-[10px] text-[rgb(var(--lm-text-muted))]">{templates.length} templates prêts à l'emploi · B2B / B2C / Saisons</p>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Rechercher un template..."
          className="pl-9 h-9 text-xs"
          data-testid="templates-search"
        />
      </div>

      {/* Filters */}
      <div className="space-y-2">
        <div>
          <p className="text-[9px] uppercase tracking-wider text-[rgb(var(--lm-text-muted))] font-bold mb-1.5 flex items-center gap-1">
            <Filter size={9} /> Audience
          </p>
          <div className="flex gap-1.5 flex-wrap">
            <button onClick={() => setFilterAudience('')}
              className={`px-2.5 py-1 rounded-full text-[10px] font-bold transition-colors ${filterAudience === '' ? 'bg-[rgb(var(--lm-gold))] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              data-testid="filter-aud-all">
              Tous
            </button>
            {facets.audiences.map(a => {
              const meta = AUDIENCE_LABELS[a] || { label: a, icon: '', color: 'gray' };
              const active = filterAudience === a;
              return (
                <button key={a} onClick={() => setFilterAudience(active ? '' : a)}
                  className={`px-2.5 py-1 rounded-full text-[10px] font-bold transition-colors ${active ? 'bg-[rgb(var(--lm-gold))] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                  data-testid={`filter-aud-${a}`}>
                  {meta.icon} {meta.label}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <p className="text-[9px] uppercase tracking-wider text-[rgb(var(--lm-text-muted))] font-bold mb-1.5">Saison</p>
          <div className="flex gap-1.5 flex-wrap">
            <button onClick={() => setFilterSaison('')}
              className={`px-2.5 py-1 rounded-full text-[10px] font-bold ${filterSaison === '' ? 'bg-[rgb(var(--lm-gold))] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
              Toutes
            </button>
            {facets.saisons.filter(s => s !== 'toutes-saisons').map(s => {
              const meta = SAISON_LABELS[s] || { label: s, icon: '' };
              const active = filterSaison === s;
              return (
                <button key={s} onClick={() => setFilterSaison(active ? '' : s)}
                  className={`px-2.5 py-1 rounded-full text-[10px] font-bold ${active ? 'bg-[rgb(var(--lm-gold))] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                  {meta.icon} {meta.label}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <p className="text-[9px] uppercase tracking-wider text-[rgb(var(--lm-text-muted))] font-bold mb-1.5">Occasion</p>
          <div className="flex gap-1.5 flex-wrap">
            <button onClick={() => setFilterOccasion('')}
              className={`px-2.5 py-1 rounded-full text-[10px] font-bold ${filterOccasion === '' ? 'bg-[rgb(var(--lm-gold))] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
              Toutes
            </button>
            {facets.occasions.map(o => {
              const label = OCCASION_LABELS[o] || o;
              const active = filterOccasion === o;
              return (
                <button key={o} onClick={() => setFilterOccasion(active ? '' : o)}
                  className={`px-2.5 py-1 rounded-full text-[10px] font-bold ${active ? 'bg-[rgb(var(--lm-gold))] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        {(filterAudience || filterSaison || filterOccasion || search) && (
          <button onClick={clearFilters} className="text-[10px] text-red-600 underline font-semibold">
            ✕ Effacer les filtres
          </button>
        )}
      </div>

      {/* Templates Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {visibleTemplates.length === 0 && (
          <div className="col-span-full text-center py-10 text-[rgb(var(--lm-text-muted))] text-xs">
            Aucun template avec ces filtres.
          </div>
        )}
        {visibleTemplates.map(tpl => {
          const audMeta = AUDIENCE_LABELS[tpl.audience] || { icon: '', color: 'gray' };
          const saiMeta = SAISON_LABELS[tpl.saison] || { icon: '' };
          return (
            <div key={tpl.id}
              className="bg-[rgb(var(--lm-card))] border border-[rgb(var(--lm-border))]/30 rounded-xl p-3 hover:shadow-md transition-shadow"
              data-testid={`tpl-${tpl.id}`}>
              <div className="flex items-start justify-between mb-2">
                <p className="text-xs font-bold text-[rgb(var(--lm-text))] flex-1 leading-tight">{tpl.name}</p>
                <span className="text-base ml-2 shrink-0">{audMeta.icon}</span>
              </div>
              <p className="text-[10px] text-[rgb(var(--lm-text-muted))] mb-2 line-clamp-1">{tpl.subject}</p>
              <div className="flex gap-1 flex-wrap mb-3">
                <span className="px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[9px] font-bold">{audMeta.icon} {audMeta.label?.split('·')[1]?.trim() || tpl.audience}</span>
                {tpl.saison !== 'toutes-saisons' && (
                  <span className="px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[9px] font-bold">{saiMeta.icon} {saiMeta.label}</span>
                )}
                <span className="px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-700 text-[9px] font-bold">{OCCASION_LABELS[tpl.occasion] || tpl.occasion}</span>
              </div>
              <div className="flex gap-1.5">
                <Button size="sm" variant="outline" onClick={() => setPreview(tpl)}
                  className="flex-1 h-7 text-[10px]" data-testid={`preview-${tpl.id}`}>
                  <Eye size={10} className="mr-1" /> Aperçu
                </Button>
                <Button size="sm" onClick={() => applyTemplate(tpl)}
                  className="flex-1 h-7 text-[10px] bg-[rgb(var(--lm-gold))] text-[rgb(var(--lm-navy))] font-bold"
                  data-testid={`use-${tpl.id}`}>
                  <Send size={10} className="mr-1" /> Utiliser
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Preview Modal */}
      {preview && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setPreview(null)} data-testid="preview-modal">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="px-5 py-3 border-b flex items-center justify-between bg-gradient-to-r from-amber-50 to-white">
              <div>
                <p className="text-sm font-bold text-gray-900">{preview.name}</p>
                <p className="text-[10px] text-gray-500 mt-0.5">{preview.subject}</p>
              </div>
              <button onClick={() => setPreview(null)} className="p-1 hover:bg-gray-100 rounded-lg">
                <X size={16} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 bg-gray-50">
              <div className="bg-white rounded-xl p-4 border whitespace-pre-wrap text-sm text-gray-800 leading-relaxed font-sans">
                {preview.body}
              </div>
            </div>
            <div className="px-5 py-3 border-t bg-gray-50 flex gap-2 justify-end">
              <Button size="sm" variant="outline" onClick={() => setPreview(null)} className="text-xs">Fermer</Button>
              <Button size="sm" onClick={() => applyTemplate(preview)} className="bg-[rgb(var(--lm-gold))] text-[rgb(var(--lm-navy))] font-bold text-xs"
                data-testid="modal-use-template">
                <Sparkles size={12} className="mr-1" /> Utiliser ce template
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
